import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { DevicePhoneMobileIcon, EnvelopeIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';

import Checkbox from '@/components/Forms/Checkbox';
import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import InnerTextInput from '@/components/Forms/InnerTextInput';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import { ClientButton } from '@/components/UI/Button';
import { useInterval } from '@/renderer/utils/hooks/useInterval';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import useMe from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

const CODE_REGEX = /^[0-9]{6}$/;
const EMAIL_REGEX = /\S+@\S+\.\S+/;
// How long is a code valid until it expires?
const VERIFY_VALID_TIME = 24 * 60 * 60 * 1000;
// How long should the "get a new code" button be grayed out?
const RESEND_WAIT_TIME = 60 * 1000;

// Types
type FormContactsData = {
  code?: string;
  phone?: string;
  email?: string;
  acceptSms?: boolean;
  currentPassword?: string;
};

type Fields = 'email' | 'contactSecondary' | 'contactTertiary';

type UpdateContactsData<Field extends Fields> = {
  [key in Field]?: API.User[Field];
} & { [key in `${Field}Code`]?: string };

type UpdateContactsRequest<F extends Fields = 'contactSecondary'> = RequestSchema<
  API.WithData<API.User>,
  API.WithData<UpdateContactsData<F>, { resend?: Fields; cancel?: Fields }>
>;

// API requests
const updateContactsRequest: UpdateContactsRequest = {
  name: 'updateContactsRequest',
  input: '/api/v1/user/update/me?v2Verify=true',
  // input: '/api/v1/user/update/me',
  method: 'POST',
};

export default function UpdateContactsForm<const Field extends Fields>({
  onSuccess,
  setUser,
  user,
  name,
  onlyVerify,
  full,
}: {
  full?: boolean;
  onlyVerify?: boolean;
  onSuccess?: () => void | Promise<void>;
  setUser: (newUser: API.User | null) => void;
  user: API.User;
  name: Field;
  needsPassword?: boolean;
}) {
  const userValue = user?.[name as Fields] || '';
  const metaValue = user?.meta?.[name];
  const metaStatusValue = user?.meta?.[`${name}Status` as const];
  const metaUnverifiedDateValue = user?.meta?.[`${name}Unverified` as const];
  const metaUnverifiedDate = metaUnverifiedDateValue
    ? typeof metaUnverifiedDateValue === 'number'
      ? metaUnverifiedDateValue
      : Date.parse(metaUnverifiedDateValue)
    : -Infinity;
  const now = Date.now();
  const metaUnverifiedTimeDiff = now - metaUnverifiedDate;
  const isReadyToReceiveCode = !!metaValue && !!metaUnverifiedDate;
  const [isCodeExpired, setIsCodeExpired] = useState(
    isReadyToReceiveCode && metaUnverifiedTimeDiff > VERIFY_VALID_TIME,
  );
  const [disableResendButtonFor, setDisableResendButtonFor] = useState(
    (isReadyToReceiveCode &&
      Math.max(0, Math.ceil((RESEND_WAIT_TIME - metaUnverifiedTimeDiff) / 1000))) ||
      0,
  );
  const fieldValue = isReadyToReceiveCode ? metaValue : userValue;

  const [, , refetchUser] = useMe(false, true);

  // Request hooks

  const request = useRequest(updateContactsRequest as UpdateContactsRequest<Field>, {
    manual: true,
  });

  // Timers

  const [countdownDelay, setCountdownDelay] = useState(1000);

  const countdownCallback = useCallback(() => {
    const innerNow = Date.now();
    const innerMetaUnverifiedTimeDiff = innerNow - metaUnverifiedDate;

    setDisableResendButtonFor(
      (isReadyToReceiveCode &&
        Math.max(0, Math.ceil((RESEND_WAIT_TIME - innerMetaUnverifiedTimeDiff) / 1000))) ||
        0,
    );
    setIsCodeExpired(isReadyToReceiveCode && innerMetaUnverifiedTimeDiff > VERIFY_VALID_TIME);

    if (isReadyToReceiveCode && innerMetaUnverifiedTimeDiff > RESEND_WAIT_TIME) {
      // Stop the interval
      setCountdownDelay(0);
    }
  }, [isReadyToReceiveCode, metaUnverifiedDate]);

  useInterval(countdownCallback, countdownDelay);

  // Work

  const [selectedTab, setSelectedTab] = useState<number>(EMAIL_REGEX.test(fieldValue) ? 1 : 0);

  const [isSubmitted, setIsSubmitted] = useState<boolean | 'resend'>(false);

  const form = useForm<WithFormErrors<FormContactsData>>();
  const phoneWatch = form.watch('phone');
  const emailWatch = form.watch('email');
  const codeWatch = form.watch('code');
  const watchedValue =
    (selectedTab ? emailWatch : phoneWatch) === undefined
      ? fieldValue
      : selectedTab
        ? emailWatch
        : phoneWatch;

  // If they clicked a link from email to verify, pre-fill and submit the form
  // @todo Change the email template so:
  // 1. it properly outputs email address so we can validate it is the right one,
  // 2. uses fieldCode format instead of emailCode for all of them
  const searchParams = usePageContext().urlParsed?.search as
    | ({ [key in `${Fields}Code`]?: string } & { [key in `${Fields}Email`]?: string } & {
        email?: string;
      })
    | undefined;

  const autoSubmitted = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    if (
      searchParams?.emailCode &&
      ((!searchParams?.email && form.getValues().email) ||
        form.getValues().email === searchParams?.email)
    ) {
      form.setValue('code', searchParams.emailCode);

      if (autoSubmitted.current === false) {
        const pageCodeFields = document.getElementsByName('code');

        if (pageCodeFields.length === 1) {
          autoSubmitted.current = true;
          handleFormSubmit();
        }
      }
    }
  }, [searchParams?.emailCode, searchParams?.email]);

  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isSubmitted]);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useUpdateEffect(() => {
    form.resetField('code');
    form.clearErrors();
    request.dispatch({ action: 'SET', values: { error: null } });
  }, [selectedTab]);

  // Refetch user periodically to see if the SMS failed

  const refetchRefTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    refetchUser();

    return () => {
      if (refetchRefTimer.current) {
        clearInterval(refetchRefTimer.current);
      }
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    if (
      refetchRefTimer.current &&
      metaStatusValue?.to === phoneWatch &&
      metaStatusValue?.state !== 'PENDING'
    ) {
      clearInterval(refetchRefTimer.current);
      refetchRefTimer.current = null;
    }
  }, [user, metaStatusValue, phoneWatch]);

  // Handlers
  const handleFormSubmit = useFormSubmit(
    {
      form: form,
      callback: async (body, event) => {
        let hasError = false;

        if (!selectedTab && body.phone && !body.acceptSms) {
          form.setError('acceptSms', {
            message: 'You must accept terms if you provide a phone number',
          });
          hasError = true;
        }

        if (hasError) {
          return;
        }

        const fieldVal: API.User[Field] = (selectedTab ? body.email : body.phone) || '';

        try {
          // Special case: "get a new code" resend or cancel buttons were used
          const buttonName = ((event?.nativeEvent as SubmitEvent)?.submitter as HTMLButtonElement)
            ?.name;
          const isCancelButton = buttonName === 'cancel';
          const isResendButton = buttonName === 'resend';

          if (isResendButton) {
            setCountdownDelay(1000);
          }

          setIsSubmitted(isResendButton ? 'resend' : true);

          const response = await request.run(
            {
              data: { [name]: fieldVal, [`${name}Code`]: body.code } as UpdateContactsData<Field>,
              meta: {
                resend: isResendButton ? name : undefined,
                cancel: isCancelButton ? name : undefined,
              },
            },
            { throwOnRun: true },
          );

          form.resetField('code');

          if (response) {
            setUser(response);

            if (
              !selectedTab &&
              body.phone &&
              (response.meta?.[`${name}Status` as const]?.to !== fieldVal ||
                response.meta?.[`${name}Status` as const]?.state === 'PENDING')
            ) {
              // Start checking to see if the SMS failed
              refetchRefTimer.current = setInterval(refetchUser, 30000);
            }
          }

          if (fieldVal && body.code) {
            await onSuccess?.();
          }
        } catch (error) {
          form.resetField('code');
          setIsSubmitted(false);

          throw error;
        }

        form.setValue(selectedTab ? 'phone' : 'email', '');
      },
    },
    [selectedTab],
  );

  const emailForm = (
    <>
      <FormControl
        form={form}
        name="email"
        label="Email address"
        defaultValue={EMAIL_REGEX.test(fieldValue) ? fieldValue : ''}
        register={{
          pattern: {
            value: EMAIL_REGEX,
            message: 'Email is not a valid email!',
          },
        }}
      />
      {/* @TODO Implement password check on email */}
      {/* {!!needsPassword &&
        userValue !== watchedValue &&
        (isCodeExpired ||
          !(!!watchedValue && isReadyToReceiveCode && metaValue === watchedValue)) && (
          <FormControl
            form={form}
            name="currentPassword"
            label="Current password"
            wrap={PasswordInput}
            required
          />
        )} */}
    </>
  );

  return (
    <form onSubmit={handleFormSubmit} method="POST" className="space-y-4">
      {onlyVerify && !!fieldValue ? (
        <h5 className="flex items-center space-x-2 border-zinc-900/5 border-b pb-2 text-xs text-zinc-700">
          {selectedTab ? (
            <DevicePhoneMobileIcon height={20} className="size-5 text-inherit" aria-hidden="true" />
          ) : (
            <EnvelopeIcon height={20} className="size-5 text-inherit" aria-hidden="true" />
          )}
          <span>
            {userValue === fieldValue
              ? `Successfully verified ${fieldValue}`
              : `Verify ${fieldValue}`}
          </span>
        </h5>
      ) : name === 'email' ? (
        emailForm
      ) : (
        <TabGroup selectedIndex={selectedTab} onChange={setSelectedTab}>
          <TabList className="space-x-6 border-zinc-900/5 border-b">
            <Tab className="inline-flex items-center space-x-2 border-zinc-700 pb-2 text-xs text-zinc-400 hover:border-zinc-500 hover:border-b hover:text-zinc-500 aria-selected:border-b aria-selected:text-zinc-700 dark:border-white dark:aria-selected:text-white">
              <DevicePhoneMobileIcon
                height={20}
                className="size-5 text-inherit"
                aria-hidden="true"
              />
              <span>Phone (SMS)</span>
            </Tab>
            <Tab className="inline-flex items-center space-x-2 border-zinc-700 pb-2 text-xs text-zinc-400 hover:border-zinc-500 hover:border-b hover:text-zinc-500 aria-selected:border-b aria-selected:text-zinc-700 dark:border-white dark:aria-selected:text-white">
              <EnvelopeIcon height={20} className="size-5 text-inherit" aria-hidden="true" />
              <span>Email</span>
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel className="my-4 space-y-4">
              <Controller
                render={({ field }) => (
                  <>
                    <FormControl form={form} name="phone" label="Phone (SMS)">
                      <PhoneInput
                        inputComponent={InnerTextInput}
                        defaultCountry="US"
                        onChange={(what) => field.onChange(what || '')}
                        value={field.value}
                        className="relative"
                        focusInputOnCountrySelection
                      />
                    </FormControl>
                  </>
                )}
                control={form.control}
                name="phone"
                defaultValue={EMAIL_REGEX.test(fieldValue) ? '' : fieldValue}
                rules={{
                  validate: {
                    required: (value) => {
                      if (value && !isValidPhoneNumber(value)) {
                        return 'Value is not a valid phone number!';
                      }
                    },
                  },
                }}
              />
              <FormControl
                form={form}
                id={`${name}AcceptSms`}
                name="acceptSms"
                label={
                  <>
                    I would like to receive RAS’s alerts before releases at the phone number
                    provided. Msg freq. varies. Msg & data rates may apply. Reply HELP for help or
                    STOP to cancel. I have read the{' '}
                    <a href="/terms" target="_blank" rel="noreferrer">
                      RAS Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                    .
                  </>
                }
                wrap={Checkbox}
                inline
                defaultChecked={!!fieldValue && !selectedTab}
              />
            </TabPanel>
            <TabPanel>{emailForm}</TabPanel>
          </TabPanels>
        </TabGroup>
      )}
      {!!metaStatusValue?.state &&
        metaStatusValue.state !== 'PENDING' &&
        metaStatusValue.state !== 'DELIVERED' && (
          <aside className="bg-amber-50 p-4 dark:bg-amber-950/50">
            <div className="flex flex-row items-start space-x-2 text-amber-700 text-sm dark:text-amber-300">
              <span>
                Text (SMS) release alerts aren’t currently being delivered. We’ve paused SMS —{' '}
                <a
                  href="/settings?scrollTo=telegram"
                  className="text-amber-800 underline dark:text-amber-200"
                >
                  connect Telegram
                </a>{' '}
                or add an email to keep getting notified the moment picks drop.
              </span>
            </div>
          </aside>
        )}
      {!!watchedValue && isReadyToReceiveCode && metaValue === watchedValue && (
        <>
          {isCodeExpired ? (
            <aside className="bg-amber-50 p-4 dark:bg-amber-950/50">
              <div className="flex flex-row items-center space-x-2 text-amber-700 text-sm dark:text-amber-300">
                <span>
                  Your code expired for changing this to "{metaValue}". Please resubmit to get a new
                  code.
                </span>
              </div>
            </aside>
          ) : (
            <>
              <aside className="bg-green-50 p-4 dark:bg-green-950/50">
                <div className="flex flex-row items-end space-x-2 text-green-700 text-sm dark:text-green-300">
                  <span className="shrink grow-0">
                    <FormControl
                      form={form}
                      name="code"
                      label="Code"
                      inputMode="numeric"
                      minLength={6}
                      maxLength={6}
                      className="box-content w-[6ch] text-center font-mono"
                      register={{
                        pattern: {
                          value: CODE_REGEX,
                          message: 'Code must be 6 digits',
                        },
                      }}
                      autoComplete="off"
                      aria-autocomplete="none"
                      autoFocus={!!searchParams?.emailCode}
                    />
                  </span>
                  <span>
                    A 6-digit verification code has been sent{' '}
                    {selectedTab ? 'via email' : 'to your phone'}. Enter it here within 24 hours to
                    confirm.{' '}
                    <ClientButton
                      type="submit"
                      disabled={request.loading || disableResendButtonFor > 0}
                      loading={request.loading}
                      name="resend"
                      variant="link"
                      size="baseline"
                    >
                      Get a new code
                      {disableResendButtonFor > 0 && ` in ${disableResendButtonFor} seconds...`}
                    </ClientButton>
                  </span>
                </div>
              </aside>
            </>
          )}
          {!!userValue && (
            <aside className="bg-blue-50 p-4">
              <div className="flex flex-row items-center space-x-2 text-blue-700 text-sm">
                <span>
                  <strong className="bg-red-50">{userValue}</strong> will continue to be used until
                  you successfully verify the change.
                </span>
              </div>
            </aside>
          )}
        </>
      )}
      <aside className="bg-red-50 not-empty:p-4">
        <FormErrors form={form} request={request} ignoreMismatch={true} />
      </aside>
      {!(onlyVerify && !!fieldValue && userValue === fieldValue) && (
        <div className="space-x-4">
          <FormSubmitButton
            disabled={
              request.loading ||
              (watchedValue === fieldValue && !isCodeExpired && !codeWatch) ||
              (!watchedValue && name === 'email')
            }
            loading={request.loading}
            className={full ? '' : 'w-48'}
            inline={!full}
          >
            {userValue && watchedValue !== userValue
              ? watchedValue
                ? 'Update'
                : 'Remove'
              : isReadyToReceiveCode && metaValue === watchedValue && !isCodeExpired
                ? 'Confirm'
                : fieldValue && !watchedValue
                  ? 'Cancel'
                  : 'Add'}{' '}
            {name === 'email'
              ? 'email'
              : `${name === 'contactSecondary' ? 'second' : 'third'} contact`}
          </FormSubmitButton>

          {!!userValue && isReadyToReceiveCode && (
            <ClientButton
              type="submit"
              disabled={request.loading}
              loading={request.loading}
              name="cancel"
              variant="link"
              size="baseline"
            >
              Cancel
            </ClientButton>
          )}

          <span
            className={clsx(
              'text-zinc-500 leading-4 transition-opacity duration-500',
              isSubmitted ? 'opacity-100' : 'opacity-0',
            )}
          >
            {isSubmitted === 'resend' ? 'New code sent' : 'Contact updated successfully!'}
          </span>
        </div>
      )}
    </form>
  );
}
